/**
 * Treizième salaire : quand il se verse, et combien.
 *
 * <p>L'application en tenait la provision mensuelle — la part que l'employeur
 * met de côté, portée au coût de revient — mais ne le versait jamais. La fiche
 * de décembre sortait sans lui, et les cotisations qui s'y rapportent (AVS, AC,
 * AANP, LPP, impôt à la source) n'étaient jamais prélevées.
 *
 * <p>Deux usages coexistent en Suisse : le versement en une fois, avec la paie
 * de décembre, et le versement en deux moitiés, en juin puis en décembre. Le
 * premier est le plus répandu ; c'est celui retenu par défaut, l'entreprise
 * pouvant régler l'autre. Dans les deux cas le treizième est un élément de
 * salaire ordinaire : il supporte les mêmes charges que le reste.
 *
 * <p>Une année incomplète — une embauche en avril, un départ en septembre —
 * donne droit à la part correspondante, au prorata des mois travaillés. C'est
 * la règle de l'art. 322d al. 2 CO, appliquée au mois entamé, qui compte pour
 * un mois entier : rien n'oblige à cette générosité, mais l'usage la pratique
 * et le décompte y gagne en simplicité.
 */

/** Modes de versement proposés. */
export const MODES_13 = Object.freeze({
  decembre: { libelle: 'En une fois, en décembre', mois: [12] },
  deuxFois: { libelle: 'En deux moitiés, juin et décembre', mois: [6, 12] },
});

/** Mode retenu à défaut de réglage : l'usage le plus répandu. */
export const MODE_13_DEFAUT = 'decembre';

/**
 * Mode de versement d'une entreprise.
 *
 * @param {object} [soc] société, telle que l'écran la détient
 * @returns {string} {@code decembre} ou {@code deuxFois}
 */
export function mode13(soc) {
  const regle = soc && soc.mode13;
  return MODES_13[regle] ? regle : MODE_13_DEFAUT;
}

/** Lit l'année et le mois d'un libellé {@code AAAA-MM}. */
function decoupe(moisPaie) {
  const m = /^(\d{4})-(\d{2})$/.exec('' + (moisPaie || ''));
  return m ? { annee: +m[1], mois: +m[2] } : null;
}

/** Lit l'année et le mois d'une date suisse ou ISO ; {@code null} si absente. */
function moisDeDate(date) {
  const d = '' + (date || '');
  if (!d.trim()) {
    return null;
  }
  if (d.includes('-')) {
    const m = /^(\d{4})-(\d{2})/.exec(d);
    return m ? { annee: +m[1], mois: +m[2] } : null;
  }
  const p = d.split('.');
  return p.length >= 3 ? { annee: +p[2], mois: +p[1] } : null;
}

/**
 * Nombre de douzièmes acquis sur une année, au prorata des mois travaillés.
 *
 * @param {object} emp    employé, avec ses dates d'entrée et de sortie
 * @param {number} annee  année considérée
 * @returns {number} un entier de 0 à 12
 */
export function douziemesAcquis(emp, annee) {
  const debut = moisDeDate(emp && emp.dateEmbauche);
  const fin = moisDeDate(emp && emp.dateFin);

  // Embauché après l'année, ou parti avant : aucun droit.
  if (debut && debut.annee > annee) {
    return 0;
  }
  if (fin && fin.annee < annee) {
    return 0;
  }

  const premier = debut && debut.annee === annee ? debut.mois : 1;
  const dernier = fin && fin.annee === annee ? fin.mois : 12;
  return Math.max(0, Math.min(12, dernier - premier + 1));
}

/**
 * Montant du treizième à verser avec une paie donnée.
 *
 * <p>Le montant vaut zéro hors des mois de versement : c'est ce qui permet à
 * l'écran d'appeler cette fonction pour chaque mois sans se soucier du
 * calendrier.
 *
 * @param {object} params paramètres du calcul
 * @param {object} params.emp       employé concerné
 * @param {object} [params.soc]     société, pour son mode de versement
 * @param {string} params.moisPaie  mois de la paie, au format {@code AAAA-MM}
 * @param {number} params.mensuel   salaire mensuel de référence
 * @returns {number} le montant brut à ajouter, arrondi au centime
 */
export function treizieme({ emp, soc, moisPaie, mensuel }) {
  if (!emp || emp.sal13 !== true) {
    return 0;
  }
  const quand = decoupe(moisPaie);
  if (!quand) {
    return 0;
  }
  const versements = MODES_13[mode13(soc)].mois;
  if (!versements.includes(quand.mois)) {
    return 0;
  }

  const acquis = douziemesAcquis(emp, quand.annee);
  if (acquis <= 0 || !(mensuel > 0)) {
    return 0;
  }
  // Un versement en deux fois partage le droit acquis, il ne le double pas.
  const part = (mensuel * acquis) / 12 / versements.length;
  return Math.round(part * 100) / 100;
}
